/* Theme custom file */
import { mediaQueryLarge } from '@theme/utilities';

/**
 * Bestsellers block analytics. Every event goes through window.tbAnalytics, which owns the
 * consent gate, the theme editor guard and the dataLayer queue.
 * Spec: docs/specs/bestsellers.md
 */

/** Same value as the app's block, so web and app events line up in the warehouse. */
const SOURCE = 'home_trending';

const mediaQueryDesktop = matchMedia('(min-width: 990px)');

/** Visible share and dwell time a block needs before it counts as seen. */
const VIEW_RATIO = 0.5;
const VIEW_DELAY_MS = 1000;

const ARROW_KEYS = ['ArrowLeft', 'ArrowRight'];

/** @param {string} name @param {Record<string, unknown>} properties */
function track(name, properties) {
  window.tbAnalytics?.track(name, properties);
}

function breakpoint() {
  if (!mediaQueryLarge.matches) return 'mobile';
  return mediaQueryDesktop.matches ? 'desktop' : 'tablet';
}

/** @param {string | undefined} value */
function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

/**
 * Reads the attributes rendered on a carousel slide by snippets/bestsellers-block.liquid.
 * @param {HTMLElement} card
 */
function cardProperties(card) {
  const data = card.dataset;
  return {
    product_id: data.bestsellersProductId,
    product_name: data.bestsellersProductName,
    brand_name: data.bestsellersBrandName,
    position: toNumber(data.bestsellersPosition),
    price: toNumber(data.bestsellersPrice),
    compare_at_price: toNumber(data.bestsellersCompareAtPrice),
    currency: data.bestsellersCurrency,
  };
}

/** Sends the events of the Bestsellers block for the homepage grid it is given. */
class BestsellersTracking {
  /** @type {HTMLElement} */
  #grid;

  /** @type {HTMLElement | null} */
  #block = null;

  /**
   * Card the open quick add modal was opened from, since the modal itself sits outside the block.
   * @type {ReturnType<typeof cardProperties> | null}
   */
  #quickAddOrigin = null;

  /**
   * Last input that moved the carousel, read when it reports its first move.
   * @type {string | null}
   */
  #navMethod = null;

  #viewTimer = 0;
  #viewed = false;
  #scrolled = false;

  /**
   * Card the carousel last reported. Horizon reads previousIndex after it has synced a drag,
   * so only the index it reports can tell a move from a click, tap or drag that stays in place.
   */
  #lastIndex = 0;

  #observer = new IntersectionObserver((entries) => this.#handleVisibility(entries), { threshold: VIEW_RATIO });

  /** @param {HTMLElement} grid */
  constructor(grid) {
    this.#grid = grid;
  }

  start() {
    this.#grid.addEventListener('bestsellers:inserted', () => this.#handleInserted());
    this.#grid.addEventListener('bestsellers:hidden', (event) => this.#handleHidden(event));

    // Capture phase: Horizon components stop propagation on some of their own handlers.
    document.addEventListener('click', (event) => this.#handleClick(event), { capture: true, passive: true });
    // A middle click opens the product in a new tab without firing `click`. It leaves the quick
    // add alone, which only listens for `click`, so only the product link is tracked here.
    document.addEventListener(
      'auxclick',
      (event) => {
        if (/** @type {MouseEvent} */ (event).button === 1) this.#handleProductClick(event);
      },
      { capture: true, passive: true }
    );
    document.addEventListener('cart:update', (event) => this.#handleCartUpdate(event));

    this.#grid.addEventListener('keydown', (event) => this.#rememberNav(event));
    this.#grid.addEventListener('pointerdown', (event) => this.#rememberNav(event), { passive: true });
    this.#grid.addEventListener('slideshow:select', (event) => this.#handleSlideshowSelect(event));
  }

  /**
   * Properties every event carries. The block ones come from the attributes the placement keeps
   * up to date, so they always describe where the block stands when the event is sent.
   */
  #commonProperties() {
    const common = {
      page: 'home',
      tab: 'ventes_en_cours',
      source: SOURCE,
      market: this.#grid.dataset.market,
      hero_count: toNumber(this.#grid.dataset.heroCount) ?? 0,
      breakpoint: breakpoint(),
    };
    if (!this.#block) return common;

    const data = this.#block.dataset;
    return {
      ...common,
      block_position: toNumber(data.blockPosition),
      sales_above_block: toNumber(data.salesAboveBlock),
      feature_grid_count: toNumber(data.featureGridCount),
      products_count: toNumber(data.productsCount),
    };
  }

  /** The placement rebuilds the block when it changes row, so the observed node changes with it. */
  #handleInserted() {
    if (this.#block) this.#observer.unobserve(this.#block);
    this.#block = this.#grid.querySelector('.operation-grid__grid > .bestsellers-block');
    if (this.#block) this.#observer.observe(this.#block);
    // A rebuilt block starts a new carousel on its first card.
    this.#lastIndex = 0;
  }

  /** @param {Event} event */
  #handleHidden(event) {
    const { reason } = /** @type {CustomEvent} */ (event).detail;
    track('web_bestseller_block_hidden', { ...this.#commonProperties(), reason });
  }

  /**
   * The observer also reports states below its threshold, on the first callback and when the
   * block leaves, so the ratio decides rather than `isIntersecting`, which a sliver satisfies.
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleVisibility(entries) {
    for (const entry of entries) {
      clearTimeout(this.#viewTimer);
      if (this.#viewed || entry.intersectionRatio < VIEW_RATIO) continue;

      this.#viewTimer = setTimeout(() => {
        this.#viewed = true;
        track('web_bestseller_block_viewed', this.#commonProperties());
      }, VIEW_DELAY_MS);
    }
  }

  /** @param {Event} event */
  #handleClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const quickAdd = target.closest('.product-card-alt__quick-add');
    if (quickAdd) {
      this.#handleQuickAddClick(quickAdd);
      return;
    }

    if (this.#block?.contains(target) && target.closest('.bestsellers-block__see-more')) {
      track('web_bestseller_see_all_clicked', this.#commonProperties());
      return;
    }

    this.#handleProductClick(event);
  }

  /** @param {Event} event */
  #handleProductClick(event) {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('a[href]')) return;

    const card = this.#blockCard(target);
    if (card) track('web_bestseller_product_clicked', { ...this.#commonProperties(), ...cardProperties(card) });
  }

  /**
   * Any quick add outside the block clears the origin, so an add made from another surface is
   * never credited to the block when both show the same product.
   * @param {Element} button
   */
  #handleQuickAddClick(button) {
    const card = this.#blockCard(button);
    this.#quickAddOrigin = card ? cardProperties(card) : null;
    if (!this.#quickAddOrigin) return;

    const { product_id, brand_name, position } = this.#quickAddOrigin;
    track('web_bestseller_quick_add_clicked', { ...this.#commonProperties(), product_id, brand_name, position });
  }

  /** @param {Event} event */
  #handleCartUpdate(event) {
    const origin = this.#quickAddOrigin;
    const { detail } = /** @type {CustomEvent} */ (event);
    const form = event.target;
    if (!origin || !detail?.data || detail.data.didError) return;
    if (!(form instanceof Element) || !form.closest('quick-add-alt-dialog')) return;
    if (origin.product_id !== `gid://shopify/Product/${detail.data.productId}`) return;

    // The form reports the variant it posted as sourceId; its input can lag behind a variant change.
    const sourceId = String(detail.sourceId ?? '');
    const variantId = /^\d+$/.test(sourceId) ? sourceId : form.querySelector('input[name="id"]')?.value;

    track('web_bestseller_quick_add_completed', {
      ...this.#commonProperties(),
      product_id: origin.product_id,
      variant_id: variantId ? `gid://shopify/ProductVariant/${variantId}` : undefined,
      position: origin.position,
      quantity: toNumber(detail.data.itemCount),
    });
  }

  /**
   * Remembers how the shopper drove the carousel, before Horizon reports the move it caused.
   * @param {Event} event
   */
  #rememberNav(event) {
    const target = event.target;
    if (!(target instanceof Element) || !this.#block?.contains(target)) return;

    if (event instanceof KeyboardEvent) {
      if (ARROW_KEYS.includes(event.key)) this.#navMethod = 'keyboard';
      return;
    }
    if (target.closest('.bestsellers-block__arrow')) {
      this.#navMethod = 'arrow_click';
      return;
    }
    this.#navMethod = /** @type {PointerEvent} */ (event).pointerType === 'touch' ? 'swipe' : 'scroll';
  }

  /** @param {Event} event */
  #handleSlideshowSelect(event) {
    const target = event.target;
    if (!(target instanceof Element) || !this.#block?.contains(target)) return;

    const { index } = /** @type {CustomEvent} */ (event).detail;
    // Horizon also reports a click, tap or drag that leaves the carousel on the same card.
    const moved = index !== this.#lastIndex;
    this.#lastIndex = index;
    if (this.#scrolled || !moved) return;

    this.#scrolled = true;
    track('web_bestseller_carousel_scrolled', {
      ...this.#commonProperties(),
      card_index: index,
      // A move with no remembered gesture is a native scroll, trackpad or scrollbar.
      nav_method: this.#navMethod ?? 'scroll',
    });
  }

  /**
   * The carousel slide the element belongs to, or null when it is outside the block.
   * @param {Element} element
   */
  #blockCard(element) {
    if (!this.#block?.contains(element)) return null;
    const card = element.closest('.bestsellers-block__card');
    return card instanceof HTMLElement ? card : null;
  }
}

const grid = document.querySelector('operation-grid[data-is-homepage="true"]');
if (grid instanceof HTMLElement) new BestsellersTracking(grid).start();
